export type GuideSection = { heading: string; body: string[] };

export type Guide = {
  slug: string;
  title: string;
  metaTitle: string;
  description: string;
  keyword: string;
  updated: string;
  intro: string;
  sections: GuideSection[];
  faq: { q: string; a: string }[];
};

export const guides: Guide[] = [
  {
    slug: "natural-soap-benefits",
    title: "Natural Handmade Soap: Benefits and How to Choose One",
    metaTitle: "Natural Handmade Soap Benefits — Buyer's Guide | Jul's Skin",
    description:
      "What natural handmade soap does for your skin, how it differs from commercial bars, and how to pick the right one for dry, oily or sensitive skin.",
    keyword: "natural handmade soap",
    updated: "2026-08-20",
    intro:
      "Handmade soap keeps the glycerin that commercial bars strip out, so it cleans without leaving skin tight. Here is what to look for before you buy.",
    sections: [
      {
        heading: "Why handmade soap feels different",
        body: [
          "Cold-process soap retains natural glycerin, a humectant that draws moisture to the skin.",
          "Mass-produced detergent bars often remove glycerin for other products and replace it with hardeners and synthetic foaming agents.",
        ],
      },
      {
        heading: "Match the bar to your skin type",
        body: [
          "Dry skin: look for shea butter, coconut oil and olive oil bases.",
          "Oily or acne-prone skin: charcoal, neem and tea tree help control excess oil.",
          "Sensitive skin: choose unscented or lightly essential-oil scented bars with no synthetic fragrance.",
        ],
      },
      {
        heading: "How to make a bar last",
        body: [
          "Use a draining soap dish so the bar dries fully between uses.",
          "Keep it out of the direct water stream in the shower.",
        ],
      },
    ],
    faq: [
      {
        q: "Is handmade soap safe for the face?",
        a: "Yes, if the bar is formulated gently. Avoid heavily fragranced bars on facial skin and patch test first.",
      },
      {
        q: "How long does a handmade soap bar last?",
        a: "Typically four to six weeks of daily use when stored on a draining dish.",
      },
    ],
  },
  {
    slug: "hair-oil-routine",
    title: "Natural Hair Oil Routine for Stronger, Healthier Hair",
    metaTitle: "Natural Hair Oil Routine — Step by Step | Jul's Skin",
    description:
      "A simple weekly hair oiling routine: how much oil to use, how long to leave it in, and how to wash it out without stripping your scalp.",
    keyword: "natural hair oil routine",
    updated: "2026-08-20",
    intro:
      "Oiling works when it is consistent and gentle. This routine takes about ten minutes of active time, once or twice a week.",
    sections: [
      {
        heading: "Step 1 — Warm a small amount",
        body: ["Two to three teaspoons is enough for shoulder-length hair. Warm it between your palms, not on direct heat."],
      },
      {
        heading: "Step 2 — Massage the scalp, not just the lengths",
        body: [
          "Use fingertips in slow circles for three to five minutes to support blood flow.",
          "Work the remainder through mid-lengths and ends, where hair is oldest and driest.",
        ],
      },
      {
        heading: "Step 3 — Leave it in, then wash gently",
        body: [
          "Thirty minutes is enough for most people; overnight can be too heavy for oily scalps.",
          "Wash with a mild, sulphate-free cleanser and rinse with lukewarm water.",
        ],
      },
    ],
    faq: [
      {
        q: "How often should I oil my hair?",
        a: "Once or twice a week suits most hair types. Oily scalps do better with once a week.",
      },
      { q: "Can I oil hair every day?", a: "Daily oiling can clog follicles and attract build-up. Weekly is enough." },
    ],
  },
  {
    slug: "skincare-routine-for-beginners",
    title: "A Simple Skincare Routine for Beginners",
    metaTitle: "Simple Skincare Routine for Beginners | Jul's Skin",
    description:
      "The three steps that actually matter when you are starting out — cleanse, moisturise, protect — plus how to add products without irritating your skin.",
    keyword: "skincare routine for beginners",
    updated: "2026-08-20",
    intro:
      "A good routine is short and repeatable. Start with three steps and only add a fourth once your skin is calm and predictable.",
    sections: [
      {
        heading: "Morning: cleanse, moisturise, protect",
        body: [
          "A gentle cleanser removes overnight oil without stripping.",
          "Moisturiser locks in water; use it while skin is still slightly damp.",
          "Sunscreen is the single highest-impact step for long-term skin health.",
        ],
      },
      {
        heading: "Evening: remove the day",
        body: [
          "Cleanse to clear sunscreen, sweat and pollution.",
          "Reapply moisturiser. Skip actives on nights when skin feels sensitised.",
        ],
      },
      {
        heading: "Adding new products",
        body: [
          "Introduce one product at a time and wait two weeks.",
          "Patch test on the jawline before applying to the whole face.",
        ],
      },
    ],
    faq: [
      { q: "How long before I see results?", a: "Hydration improves in days; texture and tone usually take 8–12 weeks." },
      { q: "Do I need a toner?", a: "No. It is optional and not required for a working routine." },
    ],
  },
  {
    slug: "am-pm-skincare-routine",
    title: "AM/PM Skincare Routine: Step-by-Step Order of Application",
    metaTitle: "AM/PM Skincare Routine — Step by Step Order | Jul's Skin",
    description:
      "How to build a morning and night skincare routine — the correct order for cleanser, toner, serum, moisturiser and sunscreen, with Jul's Skin essentials.",
    keyword: "am pm skincare routine",
    updated: "2026-08-22",
    intro:
      "Morning routines protect skin through the day; night routines repair it. The products can overlap — what changes is the order and the purpose of each step.",
    sections: [
      {
        heading: "The AM routine, in order",
        body: [
          "1. Cleanse — a gentle handmade soap or mild face cleanser removes overnight oil and sweat.",
          "2. Tone — optional; use an alcohol-free toner to rebalance skin after cleansing.",
          "3. Serum — lightweight, water-based serums go before anything thicker.",
          "4. Moisturise — apply while skin is still slightly damp to trap water.",
          "5. Sunscreen — always the final skincare step in the morning, every day, indoors or out.",
        ],
      },
      {
        heading: "The PM routine, in order",
        body: [
          "1. Cleanse — remove sunscreen, pollution and sweat. Double cleanse if you wore heavy sunscreen or makeup.",
          "2. Tone — same optional step, useful if your cleanser leaves skin feeling tight.",
          "3. Treat — this is where actives belong at night: keep it to one active per evening.",
          "4. Moisturise — a slightly richer cream at night supports overnight barrier repair.",
          "5. Face or hair oil — seal everything in, and work oil through hair ends on wash-eve nights.",
        ],
      },
      {
        heading: "The rule that decides the order",
        body: [
          "Apply products from thinnest to thickest consistency. Water-based first, oil-based last.",
          "Give each layer thirty seconds to absorb before the next one — layering too fast causes pilling.",
          "Sunscreen breaks the rule: it always goes last in the morning, whatever its texture.",
        ],
      },
      {
        heading: "A starter AM/PM routine with Jul's Skin",
        body: [
          "AM: natural handmade soap or gentle cleanser, moisturiser, sunscreen.",
          "PM: cleanse, moisturiser, and a natural hair oil massaged into the scalp two evenings a week.",
          "Keep this fixed for four weeks before adding anything new, so you can tell what is working.",
        ],
      },
    ],
    faq: [
      {
        q: "Should the AM and PM routines use different products?",
        a: "Mostly the same. The two real differences are sunscreen in the morning and actives or richer creams at night.",
      },
      {
        q: "Does toner go before or after serum?",
        a: "Toner goes first, straight after cleansing, then serum.",
      },
      {
        q: "Can I skip the night routine if I am tired?",
        a: "Cleansing is the one step not to skip — leaving sunscreen and pollution on overnight is what causes congestion.",
      },
    ],
  },
];


export const getGuide = (slug: string) => guides.find((g) => g.slug === slug);
